/* Upload strategy helpers extracted from UploadModal.jsx.
 *
 * Both functions accept a `session` object:
 *   - Fresh upload: session.completedParts=[], session.bytesUploaded=0.
 *   - Resume: session pre-populated from session-status endpoint.
 *
 * Callers pass:
 *   - file: the File object (from picker or drop).
 *   - session: { s3UploadId, s3Key, bucket, partSize, completedParts } for multipart,
 *              { resumableUri, bytesUploaded } for drive-direct.
 *   - callbacks: onProgress({ loadedBytes, totalBytes, pct }), onPartComplete(part) for multipart.
 *   - controls: { awaitPause, isCancelled, registerXhr, unregisterXhr } for the multipart XHR lifecycle.
 *   - api: { apiBaseUrl, projectId, authToken } to call the backend.
 */

export async function uploadMultipart({
  file,
  session,                // { s3UploadId, s3Key, bucket, partSize, numParts, completedParts }
  onProgress,
  onPartComplete,
  awaitPause,
  isCancelled,
  registerXhr,
  unregisterXhr,
  api,                    // { apiBaseUrl, projectId, authToken }
}) {
  const { s3UploadId, s3Key, partSize, numParts, completedParts = [] } = session;
  const completedSet = new Set(completedParts.map((p) => p.PartNumber));
  const partsAccumulator = [...completedParts];

  const PRESIGN_BATCH = 10;
  for (let batchStart = 0; batchStart < numParts; batchStart += PRESIGN_BATCH) {
    const batchEnd = Math.min(batchStart + PRESIGN_BATCH, numParts);
    const needed = [];
    for (let i = batchStart; i < batchEnd; i++) {
      const partNum = i + 1;
      if (!completedSet.has(partNum)) needed.push(partNum);
    }
    if (needed.length === 0) continue;

    const presignRes = await fetch(`${api.apiBaseUrl}/api/projects/${api.projectId}/upload/presign-parts`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(api.authToken ? { Authorization: `Bearer ${api.authToken}` } : {}),
      },
      body: JSON.stringify({ s3Key, s3UploadId, partNumbers: needed }),
    });
    if (!presignRes.ok) throw new Error(`Presign failed (${presignRes.status})`);
    const { presignedUrls } = await presignRes.json();

    for (const partNum of needed) {
      if (awaitPause) await awaitPause();
      if (isCancelled && isCancelled()) throw new Error('Upload cancelled');

      const start = (partNum - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const blob = file.slice(start, end);
      const partUrl = presignedUrls[String(partNum)];

      const etag = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr._fileIndex = session.fileIndex;
        if (registerXhr) registerXhr(xhr);
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const loadedBytes = (partNum - 1) * partSize + e.loaded;
            onProgress({ loadedBytes, totalBytes: file.size });
          }
        });
        xhr.onload = () => {
          if (unregisterXhr) unregisterXhr(xhr);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.getResponseHeader('ETag'));
          } else {
            reject(new Error(`Part ${partNum} upload failed (${xhr.status})`));
          }
        };
        xhr.onerror = () => { if (unregisterXhr) unregisterXhr(xhr); reject(new Error(`Network error uploading part ${partNum}`)); };
        xhr.onabort = () => { if (unregisterXhr) unregisterXhr(xhr); reject(new Error('Upload cancelled')); };
        xhr.open('PUT', partUrl);
        xhr.send(blob);
      });

      const part = { PartNumber: partNum, ETag: etag };
      partsAccumulator.push(part);
      if (onPartComplete) await onPartComplete(part);
    }
  }

  return { completedParts: partsAccumulator };
}

export async function completeMultipart({
  s3Key, s3UploadId, parts, file, googleDriveId,
  existingXmlAction = '', warnOnExistingRouteXml = false,
  replaceExistingDriveFileIds,
  reproject = null,
  api,
}) {
  const body = {
    s3Key, s3UploadId, parts,
    filename: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
    googleDriveId,
    existingXmlAction,
    checkExistingRouteXml: warnOnExistingRouteXml ? '1' : '',
  };
  if (Array.isArray(replaceExistingDriveFileIds) && replaceExistingDriveFileIds.length > 0) {
    body.replaceExistingDriveFileIds = replaceExistingDriveFileIds;
  }
  // LAS/LAZ reprojection request: { sourceCrs: {epsg}|{wkt}, targetWkt }.
  // The backend submits a Batch job after the file lands in source/.
  if (reproject && reproject.sourceCrs && reproject.targetWkt) {
    body.reproject = reproject;
  }
  const res = await fetch(`${api.apiBaseUrl}/api/projects/${api.projectId}/upload/complete-multipart`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(api.authToken ? { Authorization: `Bearer ${api.authToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const result = await res.json().catch(() => null);
  if (res.status === 409 && result?.conflictPlan) {
    const err = new Error('route_xml_conflict');
    err.type = 'route_xml_conflict';
    err.payload = result;
    throw err;
  }
  if (!res.ok || !result) throw new Error(result?.error || `Complete-multipart failed (${res.status})`);
  return result;
}

export async function initMultipart({ file, googleDriveId, replaceExistingDriveFileIds, targetPrefix, api }) {
  const body = {
    filename: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
    googleDriveId,
  };
  if (Array.isArray(replaceExistingDriveFileIds) && replaceExistingDriveFileIds.length > 0) {
    body.replaceExistingDriveFileIds = replaceExistingDriveFileIds;
  }
  // Traffic Signs imagery: images/ or camera-data/ (server-validated). LAS omits it.
  if (targetPrefix) {
    body.targetPrefix = targetPrefix;
  }
  const res = await fetch(`${api.apiBaseUrl}/api/projects/${api.projectId}/upload/init-multipart`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(api.authToken ? { Authorization: `Bearer ${api.authToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Multipart init failed (${res.status})`);
  }
  return res.json();
}

export async function initDrive({ file, googleDriveId, replaceExistingDriveFileIds, api }) {
  const body = {
    filename: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
    googleDriveId,
  };
  if (Array.isArray(replaceExistingDriveFileIds) && replaceExistingDriveFileIds.length > 0) {
    body.replaceExistingDriveFileIds = replaceExistingDriveFileIds;
  }
  const res = await fetch(`${api.apiBaseUrl}/api/projects/${api.projectId}/upload/init-drive`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(api.authToken ? { Authorization: `Bearer ${api.authToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Drive init failed (${res.status})`);
  }
  return res.json();
}

export async function confirmDrive({
  driveFileId, file, googleDriveId, resumableUri, replaceExistingDriveFileIds, api,
}) {
  const body = {
    driveFileId,
    filename: file.name,
    fileSize: file.size,
    googleDriveId,
    resumableUri,
  };
  if (Array.isArray(replaceExistingDriveFileIds) && replaceExistingDriveFileIds.length > 0) {
    body.replaceExistingDriveFileIds = replaceExistingDriveFileIds;
  }
  const res = await fetch(`${api.apiBaseUrl}/api/projects/${api.projectId}/upload/confirm-drive`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(api.authToken ? { Authorization: `Bearer ${api.authToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  return res.json().catch(() => null);
}

export async function fetchSessionStatus({ strategy, sessionId, api }) {
  const res = await fetch(`${api.apiBaseUrl}/api/projects/${api.projectId}/upload/session-status`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(api.authToken ? { Authorization: `Bearer ${api.authToken}` } : {}),
    },
    body: JSON.stringify({ strategy, sessionId }),
  });
  if (!res.ok) throw new Error(`session-status failed (${res.status})`);
  return res.json();
}
